import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  Divider,
  Chip,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Preview as PreviewIcon,
  History,
  RestoreFromTrash,
  Code,
  Close
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import emailTemplatesService from '../../services/emailTemplatesService';
import { TemplateType, TemplateTypeLabels } from '../../types/emailTemplate.types';
import type {
  CreateEmailTemplateDto,
  UpdateEmailTemplateDto,
  PreviewEmailTemplateResponse,
  StoricoVersione
} from '../../types/emailTemplate.types';
import { getErrorMessage } from '../../utils/api';

const AVAILABLE_VARIABLES = [
  'nomeCliente', 'cognomeCliente', 'emailCliente',
  'tipologiaScadenza', 'dataScadenza', 'giorniRimanenti',
  'numeroDocumento', 'note', 'linkScadenza',
  'nomeAgenzia', 'emailAgenzia', 'telefonoAgenzia'
];

const DEFAULT_SAMPLE_DATA = {
  nomeCliente: 'Mario',
  cognomeCliente: 'Rossi',
  emailCliente: 'mario.rossi@example.com',
  tipologiaScadenza: 'Attestati Autorizzativi',
  dataScadenza: '2026-04-01',
  giorniRimanenti: 30,
  numeroDocumento: 'DOC-2026-001',
  note: 'Nota di esempio',
  linkScadenza: 'https://app.example.com/scadenze/123',
  nomeAgenzia: 'Agenzia Demo',
  emailAgenzia: 'info@agenzia.it',
  telefonoAgenzia: '06 1234567'
};

const EmailTemplateFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!id;

  const [formData, setFormData] = useState<CreateEmailTemplateDto>({
    nome: '',
    tipo: TemplateType.REMINDER_SCADENZA,
    oggetto: '',
    htmlBody: '',
    textBody: '',
    descrizione: '',
    variabiliDisponibili: []
  });

  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewEmailTemplateResponse | null>(null);
  const [restoreDialog, setRestoreDialog] = useState<{ open: boolean; version: StoricoVersione | null }>({
    open: false,
    version: null
  });

  const { data: template, isLoading } = useQuery({
    queryKey: ['email-template', id],
    queryFn: () => emailTemplatesService.getById(id!),
    enabled: isEdit
  });

  useEffect(() => {
    if (template) {
      setFormData({
        nome: template.nome,
        tipo: template.tipo,
        oggetto: template.oggetto,
        htmlBody: template.htmlBody,
        textBody: template.textBody || '',
        descrizione: template.descrizione || '',
        variabiliDisponibili: template.variabiliDisponibili || []
      });
    }
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: (data: CreateEmailTemplateDto | UpdateEmailTemplateDto) =>
      isEdit
        ? emailTemplatesService.update(id!, data as UpdateEmailTemplateDto)
        : emailTemplatesService.create(data as CreateEmailTemplateDto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      queryClient.invalidateQueries({ queryKey: ['email-template', id] });
      enqueueSnackbar(
        isEdit ? 'Template aggiornato con successo' : 'Template creato con successo',
        { variant: 'success' }
      );
      navigate('/email-templates');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il salvataggio'), { variant: 'error' });
    }
  });

  const previewMutation = useMutation({
    mutationFn: () => emailTemplatesService.preview(id!, { sampleData: DEFAULT_SAMPLE_DATA }),
    onSuccess: (data) => {
      setPreviewResult(data);
      setPreviewDialog(true);
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'anteprima'), { variant: 'error' });
    }
  });

  const restoreMutation = useMutation({
    mutationFn: (versionNumber: number) => emailTemplatesService.restoreVersion(id!, versionNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-template', id] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setRestoreDialog({ open: false, version: null });
      enqueueSnackbar('Versione ripristinata con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il ripristino'), { variant: 'error' });
    }
  });

  const handleChange = (field: keyof CreateEmailTemplateDto, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.oggetto.trim() || !formData.htmlBody.trim()) {
      enqueueSnackbar('Compila tutti i campi obbligatori', { variant: 'warning' });
      return;
    }
    saveMutation.mutate(formData);
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('htmlBody-editor') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.htmlBody;
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      handleChange('htmlBody', newText);
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + variable.length + 4;
      }, 0);
    } else {
      handleChange('htmlBody', formData.htmlBody + `{{${variable}}}`);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento template..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/email-templates')} variant="outlined">
              Indietro
            </Button>
            <Typography variant="h4" component="h1" fontWeight={600}>
              {isEdit ? 'Modifica Template' : 'Nuovo Template'}
            </Typography>
            {template && (
              <Chip label={`v${template.versione}`} color="primary" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEdit && (
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
              >
                Anteprima
              </Button>
            )}
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
            {/* Main Content */}
            <Box>
              {/* Info Generali */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Informazioni Generali
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Nome Template *"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                  />
                  <TextField
                    select
                    label="Tipo *"
                    value={formData.tipo}
                    onChange={(e) => handleChange('tipo', e.target.value)}
                    fullWidth
                  >
                    {Object.entries(TemplateTypeLabels).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Descrizione"
                    value={formData.descrizione}
                    onChange={(e) => handleChange('descrizione', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    inputProps={{ maxLength: 500 }}
                    sx={{ gridColumn: '1 / -1' }}
                  />
                </Box>
              </Paper>

              {/* Contenuto Email */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Contenuto Email
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TextField
                  label="Oggetto Email *"
                  value={formData.oggetto}
                  onChange={(e) => handleChange('oggetto', e.target.value)}
                  fullWidth
                  sx={{ mb: 3 }}
                  inputProps={{ maxLength: 200 }}
                  helperText="Puoi usare variabili Handlebars: {{nomeCliente}}, {{tipologiaScadenza}}, etc."
                />

                {/* Variabili disponibili */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Variabili disponibili (clicca per inserire nel body):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {AVAILABLE_VARIABLES.map((v) => (
                      <Chip
                        key={v}
                        label={`{{${v}}}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => insertVariable(v)}
                        icon={<Code sx={{ fontSize: 14 }} />}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>

                <TextField
                  id="htmlBody-editor"
                  label="Corpo HTML *"
                  value={formData.htmlBody}
                  onChange={(e) => handleChange('htmlBody', e.target.value)}
                  fullWidth
                  multiline
                  rows={16}
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }
                  }}
                  helperText="Template HTML con sintassi Handlebars. Supporta: {{variabile}}, {{#if condizione}}...{{/if}}, {{formatDate data}}"
                />

                <TextField
                  label="Corpo Testo (alternativo)"
                  value={formData.textBody}
                  onChange={(e) => handleChange('textBody', e.target.value)}
                  fullWidth
                  multiline
                  rows={6}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }
                  }}
                  helperText="Versione testo puro dell'email, per client che non supportano HTML"
                />
              </Paper>

              {/* Bottone Salva */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/email-templates')}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Crea Template'}
                </Button>
              </Box>
            </Box>

            {/* Sidebar */}
            <Box>
              {/* Info Template */}
              {template && (
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Informazioni
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Versione corrente</Typography>
                      <Typography variant="body2" fontWeight={600}>v{template.versione}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Stato</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={template.abilitato ? 'Attivo' : 'Disabilitato'}
                          color={template.abilitato ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Creato il</Typography>
                      <Typography variant="body2">
                        {format(new Date(template.createdAt), 'dd/MM/yyyy HH:mm', { locale: it })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Ultimo aggiornamento</Typography>
                      <Typography variant="body2">
                        {format(new Date(template.updatedAt), 'dd/MM/yyyy HH:mm', { locale: it })}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* Storico Versioni */}
              {template && template.storicoVersioni && template.storicoVersioni.length > 0 && (
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <History color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Storico Versioni
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {template.storicoVersioni
                      .sort((a, b) => b.versione - a.versione)
                      .map((versione) => (
                      <Card key={versione.versione} variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Chip label={`v${versione.versione}`} size="small" variant="outlined" />
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {format(new Date(versione.modificatoIl), 'dd/MM/yy HH:mm', { locale: it })}
                              </Typography>
                            </Box>
                            <Tooltip title="Ripristina questa versione">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setRestoreDialog({ open: true, version: versione })}
                              >
                                <RestoreFromTrash fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Modificato da: {versione.modificatoDa}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ mt: 0.5 }}>
                            Oggetto: {versione.oggetto}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Paper>
              )}

              {/* Guida Variabili */}
              <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Guida Sintassi
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {'{{variabile}}'} - Inserisce il valore<br />
                      {'{{#if cond}}...{{/if}}'} - Condizionale<br />
                      {'{{formatDate data}}'} - Formatta data in italiano<br />
                      {'{{formatCurrency val}}'} - Formatta in EUR
                    </Typography>
                  </Alert>
                </Box>
              </Paper>
            </Box>
          </Box>
        </form>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialog}
          onClose={() => setPreviewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PreviewIcon color="primary" />
              Anteprima Email
            </Box>
            <IconButton onClick={() => setPreviewDialog(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {previewResult && (
              <Box>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">Oggetto:</Typography>
                  <Typography variant="body1" fontWeight={600}>{previewResult.oggetto}</Typography>
                </Box>
                {previewResult.variabiliMancanti && previewResult.variabiliMancanti.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Variabili mancanti nei dati di esempio: {previewResult.variabiliMancanti.join(', ')}
                  </Alert>
                )}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <div dangerouslySetInnerHTML={{ __html: previewResult.htmlBody }} />
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialog(false)}>Chiudi</Button>
          </DialogActions>
        </Dialog>

        {/* Restore Version Dialog */}
        <ConfirmDialog
          open={restoreDialog.open}
          title="Ripristina Versione"
          message={`Sei sicuro di voler ripristinare la versione v${restoreDialog.version?.versione}? La versione corrente verrà archiviata nello storico.`}
          confirmText="Ripristina"
          confirmColor="primary"
          onConfirm={() => restoreDialog.version && restoreMutation.mutate(restoreDialog.version.versione)}
          onCancel={() => setRestoreDialog({ open: false, version: null })}
          loading={restoreMutation.isPending}
        />
      </Box>
    </AppLayout>
  );
};

export default EmailTemplateFormPage;
